/**
 * SharedListViewPage Component
 * Public view of shared shopping lists (no authentication required)
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShoppingBag, 
  Check, 
  X,
  Calendar,
  Eye,
  DollarSign,
  Package,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import toast from 'react-hot-toast';
import { shareAPI } from '@/services/api';

// Category icons mapping
const categoryIcons = {
  vegetables: '🥕',
  fruits: '🍎',
  dairy: '🥛',
  meat: '🥩',
  bakery: '🍞',
  beverages: '🥤',
  snacks: '🍿',
  frozen: '🧊',
  pantry: '🥫',
  personal: '🧴',
  household: '🧹',
  other: '📦'
};

export default function SharedListViewPage() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  
  const [sharedList, setSharedList] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch shared list
  useEffect(() => {
    const fetchSharedList = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await shareAPI.getSharedList(shareId);
        setSharedList(response.data);
      } catch (error) {
        console.error('Failed to load shared list:', error);
        setError(error.response?.data?.error || 'Failed to load shared list');
        toast.error('Failed to load shared list');
      } finally {
        setIsLoading(false);
      }
    };

    if (shareId) {
      fetchSharedList();
    }
  }, [shareId]);

  // Group items by category
  const groupedItems = sharedList?.items?.reduce((acc, item) => {
    const category = item.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {}) || {};

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4 transition-colors">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 dark:text-gray-400 mt-4 font-medium">Loading shared list...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !sharedList) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4 transition-colors">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">List Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || 'This shared list may have expired or been deleted.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Home
          </button>
        </motion.div>
      </div>
    );
  }

  const { listName, items, totalItems, completedItems, totalSpent, createdAt, expiresAt, viewCount } = sharedList;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 py-8 px-4 transition-colors">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-6"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-xl">
                <ShoppingBag className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{listName}</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Shared shopping list (view-only)</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-4 border border-blue-100 dark:border-blue-800">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-1">
                <Package className="w-4 h-4" />
                <span className="text-xs font-medium">Total Items</span>
              </div>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{totalItems}</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-xl p-4 border border-green-100 dark:border-green-800">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-1">
                <Check className="w-4 h-4" />
                <span className="text-xs font-medium">Completed</span>
              </div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{completedItems}</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl p-4 border border-purple-100 dark:border-purple-800">
              <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs font-medium">Total Spent</span>
              </div>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">${totalSpent?.toFixed(2) || '0.00'}</p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 rounded-xl p-4 border border-orange-100 dark:border-orange-800">
              <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 mb-1">
                <Eye className="w-4 h-4" />
                <span className="text-xs font-medium">Views</span>
              </div>
              <p className="text-2xl font-bold text-orange-700 dark:text-orange-300">{viewCount}</p>
            </div>
          </div>

          {/* Metadata */}
          <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-gray-100 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>Shared {new Date(createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>Expires {new Date(expiresAt).toLocaleDateString()}</span>
            </div>
          </div>
        </motion.div>

        {/* Items List */}
        <div className="space-y-4">
          {Object.entries(groupedItems).map(([category, categoryItems], index) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden"
            >
              {/* Category Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{categoryIcons[category] || '📦'}</span>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white capitalize">{category}</h3>
                  <span className="ml-auto text-sm font-medium text-gray-500 dark:text-gray-400">
                    {categoryItems.length} {categoryItems.length === 1 ? 'item' : 'items'}
                  </span>
                </div>
              </div>

              {/* Items */}
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {categoryItems.map((item, idx) => (
                  <div
                    key={idx}
                    className={`px-6 py-4 flex items-center gap-4 ${
                      item.isBought ? 'bg-green-50/50 dark:bg-green-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    } transition-colors`}
                  >
                    {/* Checkbox */}
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      item.isBought 
                        ? 'bg-green-500 border-green-500' 
                        : 'border-gray-300 dark:border-gray-500'
                    }`}>
                      {item.isBought && <Check className="w-4 h-4 text-white" />}
                    </div>

                    {/* Item Details */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${
                        item.isBought ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'
                      }`}>
                        {item.name}
                      </p>
                      {item.notes && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{item.notes}</p>
                      )}
                    </div>

                    {/* Quantity & Price */}
                    <div className="flex items-center gap-4 text-sm">
                      {item.quantity > 1 && (
                        <span className="text-gray-600 dark:text-gray-400 font-medium">
                          × {item.quantity}
                        </span>
                      )}
                      {item.price && (
                        <span className={`font-bold ${
                          item.isBought ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'
                        }`}>
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Want to create your own shopping lists?
            </p>
            <button
              onClick={() => navigate('/signup')}
              className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              Get Started for Free
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
